import { signIn } from "aws-amplify/auth";
import { Leaf } from "lucide-react";
import React, { useState } from "react";

interface LoginFormProps {
  onSuccess: () => void;
  onSwitchToSignup: () => void;
  onSwitchToForgotPassword: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({
  onSuccess,
  onSwitchToSignup,
  onSwitchToForgotPassword,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await signIn({
        username: email,
        password: password,
      });

      onSuccess();
    } catch (err: any) {
      console.error("Login error:", err);
      if (err.message.includes("User is not confirmed")) {
        setError(
          "Please verify your email address first. Check your inbox for a verification code."
        );
      } else if (err.message.includes("Incorrect username or password")) {
        setError("Incorrect email or password. Please try again.");
      } else if (err.message.includes("User does not exist")) {
        setError("No account found with this email. Please sign up first.");
      } else if (err.message.includes("Password attempts exceeded")) {
        setError("Too many failed attempts. Please try again later.");
      } else {
        setError(err.message || "Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-earth-50 flex flex-col justify-center items-center py-12 px-4">
      {/* Header */}
      <div className="text-center mb-8 animate-fade-in">
        {/* Farm logo mark */}
        <div className="mx-auto w-14 h-14 bg-field rounded-full flex items-center justify-center shadow-card mb-5">
          <Leaf className="w-7 h-7 text-white" strokeWidth={2.5} />
        </div>
        <h1 className="font-display text-3xl text-earth-900">
          Waddington Park
        </h1>
        <p className="mt-2 text-earth-600 text-lg">Farm Tasks</p>
      </div>

      {/* Form Card */}
      <div className="w-full max-w-sm card p-8 animate-slide-up">
        <h2 className="font-display text-xl text-earth-900 text-center mb-6">
          Sign in to continue
        </h2>

        {error && (
          <div className="mb-5 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-alert-urgent text-sm text-center">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="label">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="password" className="label !mb-0">
                Password
              </label>
              <button
                type="button"
                onClick={onSwitchToForgotPassword}
                className="text-sm font-medium text-field hover:text-field-600 hover:underline"
              >
                Forgot password?
              </button>
            </div>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              placeholder="Enter your password"
            />
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3 text-base"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Signing in...
                </div>
              ) : (
                "Sign in"
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Create account link */}
      <div className="mt-6 w-full max-w-sm animate-fade-in" style={{ animationDelay: "100ms" }}>
        <div className="py-4 px-4 card-warm text-center">
          <p className="text-earth-600">
            New to Farm Tasks?{" "}
            <button
              type="button"
              onClick={onSwitchToSignup}
              className="font-semibold text-field hover:text-field-600 hover:underline"
            >
              Create an account
            </button>
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-12 text-center">
        <p className="text-sm text-earth-500">
          &copy; {new Date().getFullYear()} Waddington Park. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LoginForm;
